import { useCallback } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { IntegrationsClient } from "@/features/integrations/IntegrationsClient";
import {
  getInboundSummary,
  integrationsKeys,
  listInboundMessages,
  type InboundSearch,
} from "@/features/integrations/integrations.api";
import { applyParamUpdates } from "@/lib/searchParams";

// A view to watch while Datow and WLX test the TALIS link: it refreshes itself.
const REFRESH_MS = 30_000;

export const Route = createFileRoute("/_shell/integrations")({
  beforeLoad: ({ context }) => {
    if (!context.session.roles.includes("admin")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>): InboundSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      kind: str(search.kind),
      status: str(search.status),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: IntegrationsScreen,
});

function IntegrationsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();

  const summaryQuery = useQuery({
    queryKey: integrationsKeys.summary,
    queryFn: getInboundSummary,
    refetchInterval: REFRESH_MS,
  });
  const messagesQuery = useQuery({
    queryKey: integrationsKeys.messages(search),
    queryFn: () => listInboundMessages(search),
    placeholderData: keepPreviousData,
    refetchInterval: REFRESH_MS,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  return (
    <div className="wl-page" data-screen-label="Back-office Integrations">
      <IntegrationsClient
        summary={summaryQuery.data?.kinds}
        messages={messagesQuery.data?.data ?? []}
        pagination={messagesQuery.data?.pagination ?? { limit: 25, hasNextPage: false, nextCursor: null }}
        search={search}
        updateParams={updateParams}
        userId={session.userId}
        isLoading={messagesQuery.isLoading}
        isFetching={summaryQuery.isFetching || messagesQuery.isFetching}
      />
    </div>
  );
}
